import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigateToSession, getSessionIdFromUrl } from "../utils/sessionUtils";
import { saveSessionSearchState } from "../utils/sessionState";
import { formatDateForUrl } from "../utils/urlUtils";
import { useRecoilState } from 'recoil';
import { searchQuerySelector } from '../../../state/selectors/searchQuery';
import { isUserChangePriceSelector } from '../../../state/selectors/isUserChangePrice';
import { calculateNumberOfFilterSelected } from '../../../components/categoryBar/filterBtn/calculateNumberOfFilterSelected';
import styled from 'styled-components';
import { ColorTheme, TextTheme } from '../../../components/globaStyledComponents/globalStyledComponentsStyling';
import useClickOutside from '../../../hook/useClickOutside';
import { motion, AnimatePresence } from 'framer-motion';
import { searchService } from "../services/searchService";

const PopupSearchBarWrapper = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.06);
    backdrop-filter: blur(8px);
    cursor: default;
`;

const SearchInputWrapper = styled(motion.div)`
    width: calc(100% - 48px);
    max-width: 850px;
    min-height: 72px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(28px);
    border-radius: 32px;
    display: flex;
    align-items: flex-start;
    padding: 0 20px 0 24px;
    gap: 16px;
    cursor: text;
    box-shadow: 1px 3px 12px 0px rgba(0, 0, 0, 0.1),
        0 0 2px 0 rgba(0, 0, 0, 0.125);
    margin: 24px auto;
`;

const SearchInput = styled.textarea`
    flex: 1;
    min-height: 72px;
    height: 72px;
    border: none;
    background: none;
    font-size: ${TextTheme.sans_500.fontSize};
    font-family: ${TextTheme.sans_500.fontFamily};
    line-height: 24px;
    color: ${ColorTheme.Black.color};
    resize: none;
    outline: none;
    padding: 24px 0;
    margin: 0;
    border-radius: 0;
    display: flex;
    align-items: center;
    overflow-y: auto;
    max-height: 200px;
    box-sizing: border-box;

    &::placeholder {
        color: ${ColorTheme.Grey.color};
        font-family: ${TextTheme.sans_400.fontFamily};
        line-height: 24px;
    }

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${ColorTheme.Grey2.color};
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: ${ColorTheme.Grey.color};
    }
`;

const SendButton = styled.button<{ isInputEmpty: boolean }>`
    display: ${props => props.isInputEmpty ? 'none' : 'flex'};
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background-color: black;
    align-items: center;
    justify-content: center;
    padding: 0;
    min-width: 32px;
    min-height: 32px;
    cursor: pointer;
    transition: opacity 0.2s ease;
    margin-top: auto;
    margin-bottom: 20px;
    &:hover {
        opacity: 0.8;
    }
`;

interface PopupSearchBarProps {
    isOpen: boolean;
    onClose: () => void;
    initialValue?: string;
    isEditingHistory?: boolean;
    onEditSubmit?: (newQuery: string) => void;
    // Optional: trigger search requests directly
    fetchListings?: (opts: {
        place_id?: string,
        startDate: string,
        endDate: string,
        searchFilters: any,
        page: number,
        boundingBox: any,
        isMapRequest: boolean,
        textInput?: string,
        parentMessageId?: string,
        isEdit?: boolean,
    }) => void;
    searchFilters?: any;
    boundingBox?: any;
    focusedMessageId?: string | null;
    parentUserMessageId?: string | null;
}

export default function PopupSearchBar({ 
    isOpen, 
    onClose, 
    initialValue = '', 
    isEditingHistory = false,
    onEditSubmit,
    fetchListings,
    searchFilters,
    boundingBox,
    focusedMessageId,
    parentUserMessageId
}: PopupSearchBarProps) {
    const [inputValue, setInputValue] = useState(initialValue);
    const [searchQuery, setSearchQuery] = useRecoilState(searchQuerySelector);
    const [isUserChangePrice] = useRecoilState(isUserChangePriceSelector);
    const navigate = useNavigate();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const searchInputWrapperRef = useRef<HTMLDivElement>(null);
    const popupRef = useClickOutside(isOpen, onClose, 'mousedown');

    // Update input value when initialValue changes
    useEffect(() => {
        setInputValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        if (isOpen && textareaRef.current) {
            const textarea = textareaRef.current;
            textarea.focus();
            // Set cursor to end of text
            const length = textarea.value.length;
            textarea.setSelectionRange(length, length);
            // Set initial height
            textarea.style.height = '72px';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        }
    }, [isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const textarea = e.target;
        textarea.style.height = '72px';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        setInputValue(textarea.value);
    };

    const isInputEmpty = inputValue.trim().length === 0;

    const submitSearch = () => {
        try {} catch {}
        // If editing history, call the edit submit handler
        if (isEditingHistory && onEditSubmit) {
            onEditSubmit(inputValue);
            // Also update the current search query in Recoil state
            const searchQueryCopy = { ...searchQuery };
            searchQueryCopy.text = inputValue;
            setSearchQuery(searchQueryCopy);
            // Also trigger an edit search against the focused message id
            try {
                // Ensure we preserve the current conversation/session id during edits
                try {
                    const sid = getSessionIdFromUrl();
                    if (sid) {
                        searchService.setConversationId(sid);
                    }
                } catch {}
                const placeId = (searchQuery.placeId || undefined) as string | undefined;
                const arrival = searchQuery.startDate ? formatDateForUrl(searchQuery.startDate as any) : 'anyArrival';
                const departure = searchQuery.endDate ? formatDateForUrl(searchQuery.endDate as any) : 'anyDeparture';
                const effectiveParentId = parentUserMessageId || undefined;
                const payload = {
                    place_id: placeId,
                    startDate: arrival,
                    endDate: departure,
                    searchFilters: searchFilters || {},
                    page: 1,
                    boundingBox: (boundingBox ?? null) as any,
                    isMapRequest: false,
                    textInput: inputValue,
                    parentMessageId: effectiveParentId,
                    isEdit: !!effectiveParentId,
                };
                if (fetchListings) fetchListings(payload);
            } catch (e) { try {} catch {} }
            return;
        }

        // Otherwise, perform normal search
        const searchQueryCopy = { ...searchQuery };
        searchQueryCopy.text = inputValue;
        setSearchQuery(searchQueryCopy);

        const arrival = searchQuery.startDate ? formatDateForUrl(searchQuery.startDate as any) : 'anyArrival';
        const departure = searchQuery.endDate ? formatDateForUrl(searchQuery.endDate as any) : 'anyDeparture';
        // For normal searches, preserve current session instead of creating new one
        const sessionId = getSessionIdFromUrl() || navigateToSession(navigate);
        try {
            saveSessionSearchState(sessionId, {
                text: inputValue,
                startDate: arrival,
                endDate: departure,
            } as any);
        } catch {}
        // Also trigger a normal search request
        try {
            const placeId = (searchQuery.placeId || undefined) as string | undefined;
            const payload = {
                place_id: placeId,
                startDate: arrival,
                endDate: departure,
                searchFilters: searchFilters || {},
                page: 1,
                boundingBox: (boundingBox ?? null) as any,
                isMapRequest: false,
                textInput: inputValue,
                parentMessageId: undefined,
                isEdit: false,
            };
            if (fetchListings) fetchListings(payload);
        } catch (e) { try {} catch {} }
        onClose();
    };

    const handleSubmit = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!isInputEmpty) submitSearch();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <PopupSearchBarWrapper
                    ref={popupRef}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={(e) => {
                        if (searchInputWrapperRef.current && !searchInputWrapperRef.current.contains(e.target as Node)) {
                            onClose();
                        }
                    }}
                >
                    <SearchInputWrapper
                        ref={searchInputWrapperRef}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <SearchInput
                            ref={textareaRef}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                                handleSubmit(e);
                            }}
                            placeholder="Search or ask Anystay"
                            rows={1}
                        />
                        <SendButton
                            type="button"
                            isInputEmpty={isInputEmpty}
                            onClick={() => { if (!isInputEmpty) submitSearch(); }}
                        >
                            <img 
                                src="https://d292awxalydr86.cloudfront.net/Universal+icons/send.svg" 
                                alt="Send" 
                                width="10.29" 
                                height="12"
                                style={{ display: 'block', margin: 'auto' }}
                            />
                        </SendButton>
                    </SearchInputWrapper>
                </PopupSearchBarWrapper>
            )}
        </AnimatePresence>
    );
} 